import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Crown, Users, Sparkles, Info, RefreshCw, ArrowUp } from 'lucide-react';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import * as subscriptionService from '../../services/subscription';
import type { PlanType, BillingCycle } from '../../types';

interface PricingTableProps {
  onCheckoutStart?: () => void;
  onError?: (error: string) => void;
  onDowngrade?: (targetPlan: PlanType, billingCycle: BillingCycle) => void;
  onPlanChanged?: (type: 'immediate' | 'scheduled', effectiveDate?: string) => void;
  variant?: 'default' | 'landing';
}

interface FeatureRow {
  key: string;
  free: string | boolean;
  premium: string | boolean;
  duo: string | boolean;
}

interface UpgradePreview {
  targetPlan: PlanType;
  billingCycle: BillingCycle;
  amountDue: number;
  currency: string;
  credit: number;
  newPlanCost: number;
}

const PLAN_ORDER: Record<PlanType, number> = { free: 0, premium: 1, duo: 2 };

export default function PricingTable({ onCheckoutStart, onError, onDowngrade, onPlanChanged, variant = 'default' }: PricingTableProps) {
  const { t } = useTranslation('subscription');
  const { t: tc } = useTranslation('common');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('yearly');
  const [loadingPlan, setLoadingPlan] = useState<PlanType | null>(null);
  const [upgradePreview, setUpgradePreview] = useState<UpgradePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const planInfo = useSubscriptionStore((s) => s.planInfo);

  const effectivePlan = planInfo?.effectivePlan || 'free';
  const isLanding = variant === 'landing';

  const features: FeatureRow[] = [
    { key: 'personalAccounts', free: t('features.limited', { count: 1 }), premium: t('features.unlimited'), duo: t('features.unlimited') },
    { key: 'sharedAccounts', free: t('features.limited', { count: 1 }), premium: t('features.limited', { count: 1 }), duo: t('features.unlimited') },
    { key: 'budgets', free: t('features.limited', { count: 2 }), premium: t('features.unlimited'), duo: t('features.unlimited') },
    { key: 'customCategories', free: t('features.limited', { count: 3 }), premium: t('features.unlimited'), duo: t('features.unlimited') },
    { key: 'dashboard', free: t('features.dashboardBasic'), premium: t('features.dashboardFull'), duo: t('features.dashboardFull') },
    { key: 'export', free: false, premium: true, duo: true },
    { key: 'history', free: t('features.months', { count: 6 }), premium: t('features.unlimited'), duo: t('features.unlimited') },
  ];

  const hasActiveSubscription = planInfo?.subscription &&
    ['active', 'trialing'].includes(planInfo.subscription.status) &&
    planInfo.source === 'subscription';

  const formatAmount = (cents: number, currency: string) => {
    return new Intl.NumberFormat(currency === 'eur' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  const handleCheckout = async (plan: PlanType) => {
    if (plan === effectivePlan && billingCycle === planInfo?.subscription?.billingCycle) return;

    // Downgrade détecté : appeler le callback parent
    if (PLAN_ORDER[plan] < PLAN_ORDER[effectivePlan] && onDowngrade) {
      onDowngrade(plan, billingCycle);
      return;
    }

    if (plan === 'free') return;

    if (hasActiveSubscription && PLAN_ORDER[plan] > PLAN_ORDER[effectivePlan]) {
      // Upgrade avec abonnement existant : afficher l'aperçu du prorata
      setIsLoadingPreview(true);
      setLoadingPlan(plan);
      try {
        const response = await subscriptionService.previewPlanChange(plan, billingCycle);
        if (response.success && response.data) {
          setUpgradePreview({
            targetPlan: plan,
            billingCycle,
            amountDue: response.data.amountDue,
            currency: response.data.currency,
            credit: response.data.credit,
            newPlanCost: response.data.newPlanCost,
          });
        }
      } catch {
        onError?.(tc('errorOccurred'));
      } finally {
        setIsLoadingPreview(false);
        setLoadingPlan(null);
      }
      return;
    }

    setLoadingPlan(plan);
    onCheckoutStart?.();
    try {
      if (hasActiveSubscription) {
        // Abonné existant : changement de cycle programmé
        const response = await subscriptionService.changePlan(plan, billingCycle);
        if (response.success && response.data) {
          onPlanChanged?.(response.data.type, response.data.effectiveDate);
        }
      } else {
        // Nouvel abonné : checkout Stripe
        const successUrl = `${window.location.origin}/dashboard/abonnement?success=true`;
        const cancelUrl = `${window.location.origin}/dashboard/abonnement?cancelled=true`;
        const response = await subscriptionService.createCheckout(plan as 'premium' | 'duo', billingCycle, successUrl, cancelUrl);
        if (response.success && response.data?.url) {
          window.location.href = response.data.url;
        }
      }
    } catch {
      onError?.(tc('errorOccurred'));
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradePreview) return;
    setIsConfirming(true);
    try {
      const response = await subscriptionService.changePlan(upgradePreview.targetPlan, upgradePreview.billingCycle);
      if (response.success && response.data) {
        setUpgradePreview(null);
        onPlanChanged?.(response.data.type, response.data.effectiveDate);
      }
    } catch {
      onError?.(tc('errorOccurred'));
    } finally {
      setIsConfirming(false);
    }
  };

  const getDisplayPrice = (plan: PlanType): string => {
    if (plan === 'free') return t('pricing.free');
    if (plan === 'premium') {
      return billingCycle === 'monthly' ? t('pricing.premiumMonthly') : t('pricing.premiumYearlyPerMonth');
    }
    return billingCycle === 'monthly' ? t('pricing.duoMonthly') : t('pricing.duoYearlyPerMonth');
  };

  const getYearlyTotal = (plan: PlanType): string => {
    if (plan === 'premium') return t('pricing.premiumYearly');
    return t('pricing.duoYearly');
  };

  const getSavePercent = (plan: PlanType): string => {
    if (plan === 'premium') return t('pricing.premiumSavePercent');
    return t('pricing.duoSavePercent');
  };

  const plans: { type: PlanType; icon: typeof Crown; gradient: string; border: string; badge: string; bg: string; iconColor: string; btnClass: string }[] = [
    {
      type: 'free',
      icon: Sparkles,
      gradient: 'plan-surface',
      border: 'border-gray-200',
      badge: '',
      bg: 'plan-surface',
      iconColor: 'text-gray-400',
      btnClass: 'bg-gray-100 text-gray-400 cursor-default',
    },
    {
      type: 'premium',
      icon: Crown,
      gradient: 'plan-surface--premium',
      border: 'border-amber-300 ring-1 ring-amber-100',
      badge: 'popular',
      bg: '',
      iconColor: 'text-amber-500',
      btnClass: 'bg-amber-500 hover:bg-amber-600 text-white shadow-md shadow-amber-200/50',
    },
    {
      type: 'duo',
      icon: Users,
      gradient: 'plan-surface--duo',
      border: 'border-purple-300 ring-1 ring-purple-100',
      badge: 'bestValue',
      bg: '',
      iconColor: 'text-purple-500',
      btnClass: 'bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-200/50',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Toggle mensuel/annuel */}
      <div className="flex items-center justify-center">
        <div className="bg-gray-100 rounded-xl p-1 flex items-center gap-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all ${
              billingCycle === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('pricing.billedMonthly')}
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
              billingCycle === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('pricing.billedYearly')}
            <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
              {t('pricing.save', { percent: t('pricing.duoSavePercent') })}
            </span>
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map(({ type, icon: Icon, gradient, border, badge, iconColor, btnClass }) => {
          const isSamePlan = effectivePlan === type;
          const isSameCycle = planInfo?.subscription?.billingCycle === billingCycle;
          const isCurrent = !isLanding && isSamePlan && (type === 'free' || isSameCycle);

          return (
            <div
              key={type}
              className={`relative flex flex-col rounded-2xl border ${border} ${gradient || 'plan-surface'} ${isLanding ? 'p-7' : 'p-6'} transition-all hover:shadow-lg ${
                type === 'duo' ? 'md:scale-[1.03]' : ''
              }`}
            >
              {/* Badge */}
              {badge && (
                <div className={`absolute -top-4 left-1/2 -translate-x-1/2 text-white text-[10px] font-bold px-3 py-1.5 rounded-xl uppercase tracking-wider text-center leading-tight max-w-[80%] ${
                  type === 'premium' ? 'bg-amber-500' : 'bg-purple-600'
                }`}>
                  {badge === 'popular'
                    ? t('pricing.popular', { ns: 'landing' })
                    : t('pricing.bestValue', { ns: 'landing' })
                  }
                </div>
              )}

              {/* Plan name */}
              <div className="flex items-center gap-2.5 mb-4 mt-1">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  type === 'free' ? 'plan-dot' : type === 'premium' ? 'plan-dot--premium' : 'plan-dot--duo'
                }`}>
                  <Icon className={`w-5 h-5 ${iconColor}`} />
                </div>
                <h3 className={`text-lg font-bold ${type === 'free' ? 'text-gray-700' : 'text-gray-900'}`}>
                  {t(`plans.${type}`)}
                </h3>
              </div>

              {/* Price */}
              <div className="mb-5">
                <div className="flex items-baseline gap-1">
                  <span className={`font-bold ${isLanding ? 'text-4xl' : 'text-3xl'} text-gray-900`}>
                    {getDisplayPrice(type)}
                  </span>
                  {type !== 'free' && (
                    <span className="text-sm text-gray-500">
                      {t('pricing.perMonthEquiv')}
                    </span>
                  )}
                </div>
                {type !== 'free' && billingCycle === 'yearly' && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-xs text-gray-400 line-through">
                      {type === 'premium' ? t('pricing.premiumMonthly') : t('pricing.duoMonthly')}{t('pricing.perMonth')}
                    </span>
                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                      -{getSavePercent(type)}%
                    </span>
                  </div>
                )}
                {type !== 'free' && billingCycle === 'yearly' && (
                  <p className="text-xs text-gray-400 mt-1">
                    {getYearlyTotal(type)}{t('pricing.perYear')}
                  </p>
                )}
                {type === 'duo' && (
                  <p className="text-xs text-purple-600 font-medium mt-1.5">
                    {t('features.forTwoUsers')}
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="flex-1 space-y-3 mb-6">
                {features.map((feature) => {
                  const value = feature[type];
                  const isAvailable = value !== false;

                  return (
                    <li key={feature.key} className="flex items-start gap-2.5 text-sm">
                      {isAvailable ? (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          type === 'free' ? 'plan-dot' : type === 'premium' ? 'plan-dot--premium' : 'plan-dot--duo'
                        }`}>
                          <Check className={`w-3 h-3 ${
                            type === 'free' ? 'text-gray-500' : type === 'premium' ? 'text-amber-600' : 'text-purple-600'
                          }`} />
                        </div>
                      ) : (
                        <div className="plan-dot w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <X className="w-3 h-3 text-gray-300" />
                        </div>
                      )}
                      <span className={isAvailable ? 'text-gray-700' : 'text-gray-400'}>
                        {t(`features.${feature.key}`)}
                        {typeof value === 'string' && (
                          <span className="text-gray-400 ml-1">— {value}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleCheckout(type)}
                disabled={isCurrent || (type === 'free' && !onDowngrade) || loadingPlan !== null}
                className={`w-full py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                  isCurrent
                    ? 'bg-gray-100 text-gray-400 cursor-default'
                    : type === 'free' && !onDowngrade
                      ? btnClass
                      : type === 'free' && onDowngrade
                        ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        : btnClass + ' disabled:opacity-50'
                }`}
              >
                {loadingPlan === type
                  ? (isLoadingPreview ? t('planChange.loadingPreview') : tc('loading'))
                  : isCurrent
                    ? t('actions.currentPlan')
                    : type === 'free'
                      ? (isLanding
                          ? t('pricing.startFree', { ns: 'landing' })
                          : effectivePlan !== 'free'
                            ? t('actions.downgrade', { plan: t('plans.free') })
                            : t('plans.free'))
                      : PLAN_ORDER[type] < PLAN_ORDER[effectivePlan]
                        ? t('actions.downgrade', { plan: t(`plans.${type}`) })
                        : PLAN_ORDER[type] > PLAN_ORDER[effectivePlan]
                          ? t('actions.upgrade', { plan: t(`plans.${type}`) })
                          : t('actions.choosePlan')}
              </button>
            </div>
          );
        })}
      </div>

      {/* VAT note + Beta notice */}
      <div className="flex flex-col items-center gap-1.5 text-center">
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <Info className="w-3 h-3" />
          {t('pricing.vatNote')}
        </p>
        <p className="text-xs text-amber-600 font-medium">
          {t('betaNotice')}
        </p>
      </div>

      {/* Modal de confirmation upgrade avec prorata */}
      {upgradePreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !isConfirming && setUpgradePreview(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                upgradePreview.targetPlan === 'premium' ? 'bg-amber-100' : 'bg-purple-100'
              }`}>
                <ArrowUp className={`w-5 h-5 ${
                  upgradePreview.targetPlan === 'premium' ? 'text-amber-600' : 'text-purple-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('planChange.confirmTitle')}</h3>
                <p className="text-sm text-gray-500">
                  {t('planChange.confirmUpgrade', { plan: t(`plans.${upgradePreview.targetPlan}`) })}
                </p>
              </div>
            </div>

            {/* Détail du prorata */}
            <div className="space-y-3 mb-6">
              {upgradePreview.credit > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('planChange.currentPlanCredit')}</span>
                  <span className="text-green-600 font-medium">-{formatAmount(upgradePreview.credit, upgradePreview.currency)}</span>
                </div>
              )}
              {upgradePreview.newPlanCost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('planChange.newPlanCost')}</span>
                  <span className="text-gray-700 font-medium">{formatAmount(upgradePreview.newPlanCost, upgradePreview.currency)}</span>
                </div>
              )}
              <div className="border-t border-gray-100 pt-3 flex justify-between">
                <span className="font-semibold text-gray-900">{t('planChange.totalDue')}</span>
                <span className="font-bold text-lg text-gray-900">{formatAmount(upgradePreview.amountDue, upgradePreview.currency)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setUpgradePreview(null)}
                disabled={isConfirming}
                className="flex-1 btn-secondary btn-sm"
              >
                {tc('cancel')}
              </button>
              <button
                onClick={handleConfirmUpgrade}
                disabled={isConfirming}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50 ${
                  upgradePreview.targetPlan === 'premium'
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {isConfirming
                  ? <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                  : t('planChange.confirmAndPay', { amount: formatAmount(upgradePreview.amountDue, upgradePreview.currency) })
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
